/**
 * Страница дневника самочувствия пациента.
 *
 * Позволяет:
 * - создать запись за текущий день
 * - оценить каждый симптом по шкале 0-10
 * - отметить приём лекарств
 * - просмотреть историю записей
 */

import React, { useEffect, useState } from 'react';
import { patientAPI } from '../../api/client';
import { Symptom, DailyReport } from '../../types';

const DiaryPage: React.FC = () => {
  // Состояния формы
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [overallFeeling, setOverallFeeling] = useState(5);
  const [sleepQuality, setSleepQuality] = useState<number | null>(null);
  const [activityLevel, setActivityLevel] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [symptomValues, setSymptomValues] = useState<Record<number, number>>({});
  const [medications, setMedications] = useState<{ name: string; dosage: string; taken: boolean; side_effects: string }[]>([
    { name: '', dosage: '', taken: true, side_effects: '' },
  ]);
  const [reports, setReports] = useState<DailyReport[]>([]);

  // UI-состояния
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'form' | 'history'>('form');
  const [editingReport, setEditingReport] = useState<DailyReport | null>(null);
  const [totalReports, setTotalReports] = useState(0);
  const PAGE_SIZE = 14;

  // Загрузка справочника симптомов и истории при монтировании
  useEffect(() => {
    const loadData = async () => {
      try {
        const [symptomsRes, reportsRes] = await Promise.all([
          patientAPI.getSymptoms(),
          patientAPI.getDailyReports({ limit: 14 }),
        ]);
        setSymptoms(symptomsRes.data.symptoms);
        setReports(reportsRes.data.reports);
        setTotalReports(reportsRes.data.total ?? reportsRes.data.reports.length);

        // Инициализируем значения симптомов нулями
        const initial: Record<number, number> = {};
        symptomsRes.data.symptoms.forEach((s: Symptom) => { initial[s.id] = 0; });
        setSymptomValues(initial);
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
      }
    };
    loadData();
  }, []);

  /** Обработчик изменения оценки симптома */
  const handleSymptomChange = (symptomId: number, value: number) => {
    setSymptomValues({ ...symptomValues, [symptomId]: value });
  };

  /** Добавление строки лекарства */
  const addMedication = () => {
    setMedications([...medications, { name: '', dosage: '', taken: true, side_effects: '' }]);
  };

  /** Удаление строки лекарства */
  const removeMedication = (index: number) => {
    setMedications(medications.filter((_, i) => i !== index));
  };

  /** Начало редактирования записи */
  const startEdit = (report: DailyReport) => {
    setEditingReport(report);
    setOverallFeeling(report.overall_feeling);
    setSleepQuality(report.sleep_quality);
    setActivityLevel(report.activity_level);
    setNotes(report.notes || '');
    const vals: Record<number, number> = {};
    symptoms.forEach((s) => { vals[s.id] = 0; });
    report.symptoms.forEach((s) => { vals[s.symptom_id] = s.severity; });
    setSymptomValues(vals);
    setMedications(
      report.medications.length > 0
        ? report.medications.map((m) => ({ name: m.medication_name, dosage: m.dosage || '', taken: m.taken, side_effects: m.side_effects || '' }))
        : [{ name: '', dosage: '', taken: true, side_effects: '' }]
    );
    setActiveTab('form');
    setMessage('');
  };

  /** Отмена редактирования */
  const cancelEdit = () => {
    setEditingReport(null);
    setReportDate(new Date().toISOString().split('T')[0]);
    setOverallFeeling(5);
    setSleepQuality(null);
    setActivityLevel(null);
    setNotes('');
    const initial: Record<number, number> = {};
    symptoms.forEach((s) => { initial[s.id] = 0; });
    setSymptomValues(initial);
    setMedications([{ name: '', dosage: '', taken: true, side_effects: '' }]);
    setMessage('');
  };

  /** Удаление записи */
  const handleDelete = async (reportId: number) => {
    if (!confirm('Удалить эту запись?')) return;
    try {
      await patientAPI.deleteDailyReport(reportId);
      const reportsRes = await patientAPI.getDailyReports({ limit: PAGE_SIZE });
      setReports(reportsRes.data.reports);
      setTotalReports(reportsRes.data.total ?? reportsRes.data.reports.length);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Ошибка удаления');
    }
  };

  /** Загрузка следующей страницы записей */
  const loadMore = async () => {
    try {
      const res = await patientAPI.getDailyReports({ limit: PAGE_SIZE, offset: reports.length });
      setReports([...reports, ...res.data.reports]);
      setTotalReports(res.data.total ?? totalReports);
    } catch (err) {
      console.error('Ошибка загрузки:', err);
    }
  };

  /** Отправка формы дневника */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const payload = {
      overall_feeling: overallFeeling,
      sleep_quality: sleepQuality,
      activity_level: activityLevel,
      notes,
      symptoms: Object.entries(symptomValues).map(([id, severity]) => ({
        symptom_id: Number(id),
        severity,
      })),
      medications: medications.filter((m) => m.name.trim()),
    };

    try {
      if (editingReport) {
        await patientAPI.updateDailyReport(editingReport.id, payload);
        setMessage('Запись успешно обновлена!');
        setEditingReport(null);
      } else {
        await patientAPI.createDailyReport({ date: reportDate, ...payload });
        setMessage('Запись успешно сохранена!');
      }

      // Обновляем историю
      const reportsRes = await patientAPI.getDailyReports({ limit: PAGE_SIZE });
      setReports(reportsRes.data.reports);
      setTotalReports(reportsRes.data.total ?? reportsRes.data.reports.length);
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: 16 }}>Дневник самочувствия</h1>

      {/* Переключатель вкладок */}
      <div style={styles.tabs}>
        <button
          style={activeTab === 'form' ? styles.tabActive : styles.tab}
          onClick={() => { setActiveTab('form'); if (!editingReport) cancelEdit(); }}
        >
          {editingReport ? 'Редактирование' : 'Новая запись'}
        </button>
        <button
          style={activeTab === 'history' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('history')}
        >
          История ({totalReports})
        </button>
      </div>

      {/* Форма создания записи */}
      {activeTab === 'form' && (
        <form onSubmit={handleSubmit} style={styles.form}>
          {message && (
            <div style={{
              ...styles.message,
              backgroundColor: message.includes('успешно') ? '#f0fdf4' : '#fef2f2',
              color: message.includes('успешно') ? '#166534' : '#dc2626',
            }}>
              {message}
            </div>
          )}

          {/* Дата записи */}
          {!editingReport && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Дата</h3>
              <input
                type="date" value={reportDate}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setReportDate(e.target.value)}
                style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
              />
            </div>
          )}

          {/* Общая оценка самочувствия */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Общее самочувствие</h3>
            <div style={styles.sliderRow}>
              <span style={styles.sliderLabel}>0</span>
              <input
                type="range" min={0} max={10} value={overallFeeling}
                onChange={(e) => setOverallFeeling(Number(e.target.value))}
                style={styles.slider}
              />
              <span style={styles.sliderLabel}>10</span>
              <span style={styles.sliderValue}>{overallFeeling}</span>
            </div>
            <div style={styles.scaleHint}>
              0 = отличное самочувствие, 10 = максимально плохое
            </div>
          </div>

          {/* Качество сна */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              Качество сна
              {sleepQuality === null && (
                <button type="button" onClick={() => setSleepQuality(5)}
                  style={{ ...styles.addBtn, marginLeft: 12, padding: '4px 12px', fontSize: 12 }}>
                  + Добавить
                </button>
              )}
            </h3>
            {sleepQuality !== null && (
              <>
                <div style={styles.sliderRow}>
                  <span style={styles.sliderLabel}>0</span>
                  <input
                    type="range" min={0} max={10} value={sleepQuality}
                    onChange={(e) => setSleepQuality(Number(e.target.value))}
                    style={styles.slider}
                  />
                  <span style={styles.sliderLabel}>10</span>
                  <span style={styles.sliderValue}>{sleepQuality}</span>
                  <button type="button" onClick={() => setSleepQuality(null)} style={styles.removeBtn}>X</button>
                </div>
                <div style={styles.scaleHint}>
                  0 = отличный сон, 10 = крайне плохой сон
                </div>
              </>
            )}
          </div>

          {/* Физическая активность */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              Физическая активность
              {activityLevel === null && (
                <button type="button" onClick={() => setActivityLevel(5)}
                  style={{ ...styles.addBtn, marginLeft: 12, padding: '4px 12px', fontSize: 12 }}>
                  + Добавить
                </button>
              )}
            </h3>
            {activityLevel !== null && (
              <>
                <div style={styles.sliderRow}>
                  <span style={styles.sliderLabel}>0</span>
                  <input
                    type="range" min={0} max={10} value={activityLevel}
                    onChange={(e) => setActivityLevel(Number(e.target.value))}
                    style={styles.slider}
                  />
                  <span style={styles.sliderLabel}>10</span>
                  <span style={styles.sliderValue}>{activityLevel}</span>
                  <button type="button" onClick={() => setActivityLevel(null)} style={styles.removeBtn}>X</button>
                </div>
                <div style={styles.scaleHint}>
                  0 = минимальная активность, 10 = высокая активность
                </div>
              </>
            )}
          </div>

          {/* Оценка симптомов */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Симптомы</h3>
            {symptoms.map((symptom) => (
              <div key={symptom.id} style={styles.symptomRow}>
                <div style={styles.symptomName}>{symptom.name_ru}</div>
                <div style={styles.sliderRow}>
                  <input
                    type="range" min={0} max={10}
                    value={symptomValues[symptom.id] || 0}
                    onChange={(e) => handleSymptomChange(symptom.id, Number(e.target.value))}
                    style={styles.slider}
                  />
                  <span style={{
                    ...styles.sliderValue,
                    color: (symptomValues[symptom.id] || 0) >= 7 ? '#dc2626' : '#374151',
                  }}>
                    {symptomValues[symptom.id] || 0}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Приём лекарств */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Приём лекарств</h3>
            {medications.map((med, index) => (
              <div key={index} style={{ marginBottom: 12, padding: 12, backgroundColor: 'var(--bg-card-alt)', borderRadius: 8 }}>
                <div style={styles.medRow}>
                  <input
                    type="text" placeholder="Название препарата"
                    value={med.name}
                    onChange={(e) => {
                      const updated = [...medications];
                      updated[index].name = e.target.value;
                      setMedications(updated);
                    }}
                    style={{ ...styles.medInput, flex: 2 }}
                  />
                  <input
                    type="text" placeholder="Дозировка"
                    value={med.dosage}
                    onChange={(e) => {
                      const updated = [...medications];
                      updated[index].dosage = e.target.value;
                      setMedications(updated);
                    }}
                    style={{ ...styles.medInput, flex: 1 }}
                  />
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox" checked={med.taken}
                      onChange={(e) => {
                        const updated = [...medications];
                        updated[index].taken = e.target.checked;
                        setMedications(updated);
                      }}
                    />
                    Принято
                  </label>
                  {medications.length > 1 && (
                    <button type="button" onClick={() => removeMedication(index)}
                      style={styles.removeBtn}>
                      X
                    </button>
                  )}
                </div>
                <input
                  type="text" placeholder="Побочные эффекты (если есть)"
                  value={med.side_effects}
                  onChange={(e) => {
                    const updated = [...medications];
                    updated[index].side_effects = e.target.value;
                    setMedications(updated);
                  }}
                  style={{ ...styles.medInput, width: '100%', marginTop: 6, boxSizing: 'border-box' as const }}
                />
              </div>
            ))}
            <button type="button" onClick={addMedication} style={styles.addBtn}>
              + Добавить препарат
            </button>
          </div>

          {/* Заметки */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Заметки</h3>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              style={styles.textarea}
              placeholder="Опишите своё самочувствие, любые наблюдения..."
              rows={3}
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" style={{ ...styles.submitBtn, flex: 1 }} disabled={saving}>
              {saving ? 'Сохранение...' : editingReport ? 'Обновить запись' : 'Сохранить запись'}
            </button>
            {editingReport && (
              <button type="button" style={styles.cancelBtn} onClick={cancelEdit}>
                Отмена
              </button>
            )}
          </div>
        </form>
      )}

      {/* История записей */}
      {activeTab === 'history' && (
        <div>
          {reports.length === 0 ? (
            <p style={{ color: '#6b7280', padding: 20 }}>Записей пока нет</p>
          ) : (
            <>
              {reports.map((report) => (
                <div key={report.id} style={styles.reportCard}>
                  <div style={styles.reportHeader}>
                    <strong>{report.date}</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{
                        color: report.overall_feeling >= 7 ? '#dc2626' : '#374151',
                        fontWeight: 600,
                      }}>
                        Самочувствие: {report.overall_feeling}/10
                      </span>
                      <button style={styles.editBtn} onClick={() => startEdit(report)}>Ред.</button>
                      <button style={styles.deleteBtn} onClick={() => handleDelete(report.id)}>X</button>
                    </div>
                  </div>

                  {/* Сон и активность */}
                  {(report.sleep_quality !== null || report.activity_level !== null) && (
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
                      {report.sleep_quality !== null && <span>Сон: {report.sleep_quality}/10  </span>}
                      {report.activity_level !== null && <span>Активность: {report.activity_level}/10</span>}
                    </div>
                  )}

                  {/* Симптомы в записи */}
                  <div style={styles.reportSymptoms}>
                    {report.symptoms.map((s) => (
                      <span key={s.symptom_id} style={{
                        ...styles.symptomBadge,
                        backgroundColor: s.severity >= 7 ? '#fef2f2' :
                                         s.severity >= 4 ? '#fffbeb' : '#f0fdf4',
                        color: s.severity >= 7 ? '#dc2626' :
                               s.severity >= 4 ? '#b45309' : '#166534',
                      }}>
                        {s.name_ru}: {s.severity}
                      </span>
                    ))}
                  </div>

                  {/* Лекарства */}
                  {report.medications.length > 0 && (
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
                      Лекарства: {report.medications.map((m) =>
                        `${m.medication_name} (${m.taken ? 'принято' : 'пропущено'})`
                      ).join(', ')}
                    </div>
                  )}

                  {report.notes && (
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                      {report.notes}
                    </div>
                  )}
                </div>
              ))}

              {reports.length < totalReports && (
                <button onClick={loadMore} style={styles.loadMoreBtn}>
                  Загрузить ещё ({totalReports - reports.length})
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  tabs: { display: 'flex', gap: 8, marginBottom: 20 },
  tab: {
    padding: '8px 20px', border: '1px solid var(--border-color)', borderRadius: 8,
    background: 'var(--bg-card)', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)',
  },
  tabActive: {
    padding: '8px 20px', border: '1px solid #2563eb', borderRadius: 8,
    background: '#2563eb', cursor: 'pointer', fontSize: 14, color: '#fff',
  },
  form: {
    backgroundColor: 'var(--bg-card)', padding: 24, borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
  },
  message: { padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  section: { marginBottom: 24 },
  sectionTitle: { fontSize: 16, marginBottom: 12, color: 'var(--text-primary)' },
  sliderRow: { display: 'flex', alignItems: 'center', gap: 12 },
  slider: { flex: 1, height: 6, cursor: 'pointer' },
  sliderLabel: { fontSize: 12, color: 'var(--text-secondary)', width: 20 },
  sliderValue: {
    fontSize: 18, fontWeight: 700, width: 30, textAlign: 'center',
  },
  scaleHint: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 },
  symptomRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '8px 0', borderBottom: '1px solid var(--border-light)',
  },
  symptomName: { width: 180, fontSize: 14, color: 'var(--text-primary)' },
  medRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  medInput: {
    flex: 1, padding: '8px 12px', border: '1px solid var(--border-color)',
    borderRadius: 8, fontSize: 14,
  },
  checkboxLabel: { fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 },
  removeBtn: {
    background: 'var(--bg-danger)', color: '#dc2626', border: 'none',
    borderRadius: 6, padding: '6px 10px', cursor: 'pointer',
  },
  addBtn: {
    background: 'none', color: '#2563eb', border: '1px dashed #2563eb',
    borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13,
  },
  textarea: {
    width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)',
    borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%', padding: '14px', backgroundColor: '#2563eb',
    color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 16, fontWeight: 600, cursor: 'pointer',
  },
  reportCard: {
    backgroundColor: 'var(--bg-card)', padding: 16, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', marginBottom: 12,
  },
  reportHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 8,
  },
  reportSymptoms: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  symptomBadge: {
    padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500,
  },
  cancelBtn: {
    padding: '14px 24px', backgroundColor: '#6b7280',
    color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 16, fontWeight: 600, cursor: 'pointer',
  },
  editBtn: {
    background: 'var(--bg-info)', color: '#2563eb', border: 'none',
    borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
  },
  deleteBtn: {
    background: 'var(--bg-danger)', color: '#dc2626', border: 'none',
    borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12,
  },
  loadMoreBtn: {
    width: '100%', padding: '12px', backgroundColor: 'var(--bg-btn-secondary)',
    color: 'var(--text-btn-secondary)', border: 'none', borderRadius: 8,
    cursor: 'pointer', fontSize: 14, fontWeight: 500, marginTop: 8,
  },
};

export default DiaryPage;
